// 价格服务 - 获取代币的实时USD价格
export interface TokenPrice {
  symbol: string;
  price: number;
  change24h: number;
  lastUpdated: number;
}

class PriceService {
  private cache: Map<string, TokenPrice> = new Map();
  private cacheExpiry = 5 * 60 * 1000; // 5分钟缓存

  // 获取代币价格
  async getTokenPrice(symbol: string): Promise<number> {
    const cached = this.cache.get(symbol);
    if (cached && Date.now() - cached.lastUpdated < this.cacheExpiry) {
      return cached.price;
    }

    try {
      // 使用CoinGecko API获取价格
      const response = await fetch(
        `https://api.coingecko.com/api/v3/simple/price?ids=${this.getCoinGeckoId(symbol)}&vs_currencies=usd&include_24hr_change=true`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const coinId = this.getCoinGeckoId(symbol);
      const priceData = data[coinId];

      if (priceData) {
        const tokenPrice: TokenPrice = {
          symbol,
          price: priceData.usd,
          change24h: priceData.usd_24h_change || 0,
          lastUpdated: Date.now(),
        };

        this.cache.set(symbol, tokenPrice);
        return tokenPrice.price;
      }
    } catch (error) {
      console.error(`获取${symbol}价格失败:`, error);
    }

    // 如果API失败，返回默认价格
    return this.getDefaultPrice(symbol);
  }

  // 获取多个代币价格
  async getTokenPrices(symbols: string[]): Promise<Map<string, number>> {
    const prices = new Map<string, number>();
    
    // 并行获取所有价格
    const pricePromises = symbols.map(async (symbol) => {
      const price = await this.getTokenPrice(symbol);
      prices.set(symbol, price);
    });

    await Promise.all(pricePromises);
    return prices;
  }

  // 获取CoinGecko ID
  private getCoinGeckoId(symbol: string): string {
    const coinIds: { [key: string]: string } = {
      'ETH': 'ethereum',
      'USDT': 'tether',
      'USDC': 'usd-coin',
      'BNB': 'binancecoin',
      'WBTC': 'wrapped-bitcoin',
      'TRN': 'titron', // 假设Titron在CoinGecko上有记录
    };

    return coinIds[symbol] || symbol.toLowerCase();
  }

  // 获取默认价格（当API不可用时）
  private getDefaultPrice(symbol: string): number {
    const defaultPrices: { [key: string]: number } = {
      'ETH': 2000,
      'USDT': 1,
      'USDC': 1,
      'BNB': 300,
      'WBTC': 45000,
      'TRN': 0.1,
    };

    return defaultPrices[symbol] || 0;
  }

  // 清除缓存
  clearCache(): void {
    this.cache.clear();
  }

  // 获取缓存状态
  getCacheStatus(): { [key: string]: TokenPrice } {
    const status: { [key: string]: TokenPrice } = {};
    this.cache.forEach((value, key) => {
      status[key] = value;
    });
    return status;
  }
}

export const priceService = new PriceService();
